<script setup>
import { onMounted, ref, watch } from 'vue'

const props = defineProps({
  previewUrl: {
    type: String,
    required: true
  },
  content: {
    type: String,
    required: true
  },
  initialWidth: {
    default: '100%'
  },
  initialHeight: {
    default: 300
  },
  minWidth: {
    default: 300
  },
  minHeight: {
    default: 300
  },
  viewport: {
    type: String,
    default: 'desktop'
  }
})

const preview = ref(null)
const resizable = ref(null)
const resizableContainer = ref(null)
const resizing = ref(false)
const width = ref(props.initialWidth)
const height = ref(props.initialHeight)

// Define viewport presets
const viewportPresets = {
  mobile: 375,
  tablet: 768,
  desktop: 1200
}

// Update the size when viewport changes
watch(() => props.viewport, (newViewport) => {
  if (viewportPresets[newViewport]) {
    const containerWidth = resizableContainer.value.getBoundingClientRect().width
    // Only change the width if preset fits within container
    if (viewportPresets[newViewport] < containerWidth - 50) {
      width.value = viewportPresets[newViewport]
    } else {
      width.value = containerWidth - 50
    }
  }
})

onMounted(() => {
  const containerWidth = resizableContainer.value.getBoundingClientRect().width
  const containerHeight = resizableContainer.value.getBoundingClientRect().height

  // Initialize with viewport preset if available, otherwise use container width
  if (props.viewport && viewportPresets[props.viewport] && viewportPresets[props.viewport] < containerWidth - 50) {
    width.value = viewportPresets[props.viewport]
  } else {
    width.value = containerWidth - 50
  }

  height.value = containerHeight - 50
})

const updatePreview = () => {
  if (preview.value?.contentWindow) {
    // Serialize the data to prevent DataCloneError
    const messageData = {
      type: 'update',
      props: JSON.stringify({
        content: props.content,
      })
    }

    preview.value.contentWindow.postMessage(messageData, '*')
  }
}

onMounted(() => setTimeout(() => updatePreview(), 1000))

watch(() => props.content, () => updatePreview(), { deep: true })
watch(() => props.editorData, () => updatePreview(), { deep: true })
watch(() => props.contentData, () => updatePreview(), { deep: true })

// Initial update when iframe loads
const onIframeLoad = () => {
  updatePreview()
}

const onStopResizing = () => {
  resizing.value = false
  document.removeEventListener('mouseup', onStopResizing)
  document.removeEventListener('mousemove', onResizing)
}

const onResizing = (e) => {
  if (!resizing.value) return

  const rect = resizable.value.getBoundingClientRect()
  const resizableContainerRect = resizableContainer.value.getBoundingClientRect()
  const newWidth = (e.clientX - rect.left) - 10
  const newHeight = (e.clientY - rect.top) - 10

  if (newWidth > props.minWidth && newWidth < resizableContainerRect.width - 20) {
    width.value = newWidth
  }

  if (newHeight > props.minHeight && newHeight < resizableContainerRect.height - 20) {
    height.value = newHeight
  }
}

const onStartResizing = () => {
  resizing.value = true
  document.addEventListener('mouseup', onStopResizing)
  document.addEventListener('mousemove', onResizing)
}
</script>

<template>
  <div ref="resizableContainer" class="w-full p-1">
    <div
      ref="resizable"
      class="relative max-w-full mx-auto select-none"
      :style="{ height: height + 'px', width: width + 'px' }"
    >
      <div v-if="resizing" class="inset-0 absolute" />
      <iframe
        ref="preview"
        :src="previewUrl"
        class="w-full overflow-hidden rounded-lg ring-1 ring-gray-950/10"
        :style="{ pointerEvents: resizing ? 'none' : 'auto', height: height + 'px' }"
        @load="onIframeLoad"
      />
      <div
        @mousedown="onStartResizing"
        class="absolute inset-y-0 left-full hidden cursor-ew-resize items-center px-2 sm:flex"
      >
        <div class="h-8 w-1.5 rounded-full bg-gray-400" />
      </div>
    </div>
  </div>
</template>
